import { useState, useCallback } from 'react';
import {
  Box,
  Button,
  Flex,
  Heading,
  Text,
  VStack,
  HStack,
  Avatar,
  Input,
  InputGroup,
  InputLeftElement,
  Checkbox,
  Spinner,
  Icon,
  useDisclosure,
  useToast,
} from '@chakra-ui/react';
import { useNavigate } from '@tanstack/react-router';
import {
  Search,
  ChevronRight,
  SkipForward,
  ArrowLeft,
  Users,
  ContactRound,
  Sparkles,
} from 'lucide-react';
import { CompatibilityScore, TasteMatchExplanation } from '@/pages/profile/components';
import { Capacitor } from '@capacitor/core';
import { Contacts } from '@capacitor-community/contacts';
import { SEO } from '@/components/seo';
import { PageWrapper } from '@/components/layout';
import { useCompleteFriends } from '@/api/mutations/useOnboardingMutations';
import { useMatchContactsAll } from '@/api/mutations/useSocialMutations';
import { useFollowUser } from '@/api/mutations/useSocialMutations';
import { useSuggestedUsers, useSearchUsers } from '@/api/queries/useSocialQueries';
import { useGetCurrentUser } from '@/api/queries/useUserQueries';
import { userKeys } from '@/api/keys';
import { queryClient } from '@/lib/queryClient';
import { PhoneVerification } from '@/components/onboarding/PhoneVerification';
import type { UserSearchResult } from '@/types/social';

/**
 * FindFriends page - Discover and follow existing members
 * Used in both onboarding and as a standalone page from the menu.
 *
 * Features:
 * - Sync phone contacts to find friends (extracts emails and phone numbers)
 * - Browse suggested users
 * - Search for users
 * - Multi-select and bulk follow
 *
 * @param isOnboarding - If true (default), completes onboarding step and navigates to quiz.
 *                       If false, just navigates back to menu after completion.
 */
interface OnboardingFindFriendsProps {
  isOnboarding?: boolean;
}

export function OnboardingFindFriends({ isOnboarding = true }: OnboardingFindFriendsProps) {
  const navigate = useNavigate();
  const toast = useToast();
  const { mutate: completeFriends } = useCompleteFriends();
  const { mutate: matchContactsAll, isPending: isMatchingContacts } = useMatchContactsAll();
  const { mutateAsync: followUser } = useFollowUser();

  // Get current user to check if phone is already verified
  const { data: currentUser } = useGetCurrentUser();

  const [searchQuery, setSearchQuery] = useState('');
  const [selectedUserIds, setSelectedUserIds] = useState<Set<number>>(new Set());
  const [contactMatches, setContactMatches] = useState<UserSearchResult[]>([]);
  const [isFollowing, setIsFollowing] = useState(false);

  // Phone verification state
  const [showPhoneVerification, setShowPhoneVerification] = useState(false);

  const isNative = Capacitor.isNativePlatform();

  // Fetch suggested users
  const { data: suggestionsData, isLoading: isLoadingSuggestions } = useSuggestedUsers({
    enabled: true,
  });

  // Search users when query changes
  const { data: searchResults = [], isLoading: isSearching } = useSearchUsers(
    { q: searchQuery },
    { enabled: searchQuery.length >= 2 }
  );

  const suggestions = suggestionsData?.suggestions || [];
  // Merge API contact matches with locally synced ones (local takes precedence for fresh syncs)
  const apiContactMatches = suggestionsData?.contactMatches || [];
  const allContactMatches = contactMatches.length > 0
    ? contactMatches
    : apiContactMatches;

  // Determine which users to display
  const displayUsers: UserSearchResult[] = searchQuery.length >= 2
    ? searchResults
    : [...allContactMatches, ...suggestions.filter(
        (s) => !allContactMatches.some((c) => c.id === s.id)
      )];

  const toggleUserSelection = useCallback((userId: number) => {
    setSelectedUserIds((prev) => {
      const next = new Set(prev);
      if (next.has(userId)) {
        next.delete(userId);
      } else {
        next.add(userId);
      }
      return next;
    });
  }, []);

  // Show phone verification modal before syncing contacts
  const handleSyncContacts = async () => {
    if (!isNative) {
      toast({
        title: 'Not available',
        description: 'Contact sync is only available on mobile devices',
        status: 'info',
        duration: 3000,
      });
      return;
    }

    // Skip phone verification if already verified
    if (currentUser?.phoneVerified) {
      syncContacts();
      return;
    }

    // Show phone verification first
    setShowPhoneVerification(true);
  };

  // Called when phone is verified or user skips verification
  const handlePhoneVerified = (_phoneNumber: string) => {
    setShowPhoneVerification(false);
    // Invalidate user query so phoneVerified is updated
    queryClient.invalidateQueries({ queryKey: userKeys.current() });
    // Proceed with contact sync
    syncContacts();
  };

  // Called when user skips phone verification
  const handleSkipPhoneVerification = () => {
    setShowPhoneVerification(false);
    // Proceed with contact sync (email-only matching)
    syncContacts();
  };

  // Perform the actual contact sync
  const syncContacts = async () => {
    try {
      // Request permission and get contacts
      const permissionStatus = await Contacts.requestPermissions();
      if (permissionStatus.contacts !== 'granted') {
        toast({
          title: 'Permission denied',
          description: 'Please enable contacts access in settings',
          status: 'warning',
          duration: 3000,
        });
        return;
      }

      // Get contacts with BOTH emails and phone numbers
      const result = await Contacts.getContacts({
        projection: {
          emails: true,
          phones: true, // Also extract phone numbers
        },
      });

      // Extract all email addresses
      const emails: string[] = [];
      result.contacts.forEach((contact) => {
        contact.emails?.forEach((email) => {
          if (email.address) {
            emails.push(email.address.toLowerCase());
          }
        });
      });

      // Extract all phone numbers
      const phoneNumbers: string[] = [];
      result.contacts.forEach((contact) => {
        contact.phones?.forEach((phone) => {
          if (phone.number) {
            phoneNumbers.push(phone.number);
          }
        });
      });

      if (emails.length === 0 && phoneNumbers.length === 0) {
        toast({
          title: 'No contacts found',
          description: 'No email addresses or phone numbers found in your contacts',
          status: 'info',
          duration: 3000,
        });
        return;
      }

      // Match contacts using the new endpoint (both email and phone)
      matchContactsAll(
        { emails, phone_numbers: phoneNumbers, country_code: 'US' },
        {
          onSuccess: (data) => {
            setContactMatches(data.matches);
            if (data.matches.length > 0) {
              toast({
                title: `Found ${data.matches.length} friend${data.matches.length > 1 ? 's' : ''}!`,
                description: 'Select who you want to follow',
                status: 'success',
                duration: 3000,
              });
            } else {
              toast({
                title: 'No matches found',
                description: 'None of your contacts are on Paire yet',
                status: 'info',
                duration: 3000,
              });
            }
          },
          onError: () => {
            toast({
              title: 'Error',
              description: 'Failed to match contacts. Please try again.',
              status: 'error',
              duration: 3000,
            });
          },
        }
      );
    } catch (error: unknown) {
      console.error('Contact sync error:', error);
      const errorMessage =
        error && typeof error === 'object' && 'message' in error
          ? String((error as { message: string }).message)
          : 'Failed to sync contacts';
      toast({
        title: 'Error',
        description: errorMessage,
        status: 'error',
        duration: 3000,
      });
    }
  };

  const handleFollowSelected = async () => {
    if (selectedUserIds.size === 0) {
      handleContinue();
      return;
    }

    setIsFollowing(true);
    const userIds = Array.from(selectedUserIds);

    try {
      // Follow all selected users in parallel
      await Promise.all(
        userIds.map((userId) =>
          followUser({ toUser: userId, relationshipType: 'follow' })
        )
      );

      toast({
        title: 'Following!',
        description: `You're now following ${userIds.length} user${userIds.length > 1 ? 's' : ''}`,
        status: 'success',
        duration: 2000,
      });

      handleContinue();
    } catch {
      toast({
        title: 'Error',
        description: 'Some follows may have failed. Please try again.',
        status: 'error',
        duration: 3000,
      });
    } finally {
      setIsFollowing(false);
    }
  };

  const handleContinue = () => {
    if (isOnboarding) {
      completeFriends(undefined, {
        onSettled: () => {
          navigate({ to: '/onboarding/quiz' });
        },
      });
    } else {
      // Menu context - just go back
      toast({
        title: 'Done!',
        status: 'success',
        duration: 2000,
      });
      navigate({ to: '/menu' });
    }
  };

  const handleSkip = () => {
    if (isOnboarding) {
      completeFriends(undefined, {
        onSettled: () => {
          navigate({ to: '/onboarding/quiz' });
        },
      });
    } else {
      navigate({ to: '/menu' });
    }
  };

  return (
    <PageWrapper hasFixedHeader={false}>
      <SEO title="Connect with Members" />

      {/* Phone Verification Modal */}
      {showPhoneVerification && (
        <Box
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          bg="blackAlpha.800"
          zIndex={10}
          display="flex"
          alignItems="center"
          justifyContent="center"
          p={4}
        >
          <Box bg="white" borderRadius="xl" p={6} maxW="400px" width="100%">
            <VStack spacing={4}>
              <Heading size="md" color="gray.900" textAlign="center">
                Verify Your Phone
              </Heading>
              <Text fontSize="sm" color="gray.600" textAlign="center">
                Add your phone number so friends can find you on Paire
              </Text>
              <PhoneVerification
                onVerified={handlePhoneVerified}
                onSkip={handleSkipPhoneVerification}
              />
            </VStack>
          </Box>
        </Box>
      )}

      {/* Back/Skip button */}
      <Flex justify={isOnboarding ? 'flex-end' : 'flex-start'} p={4}>
        <Button
          variant="ghost"
          color="var(--canvas-text-2)"
          leftIcon={!isOnboarding ? <ArrowLeft size={16} /> : undefined}
          rightIcon={isOnboarding ? <SkipForward size={16} /> : undefined}
          onClick={handleSkip}
          _hover={{ color: 'var(--canvas-text)' }}
          data-testid="friends-skip"
        >
          {isOnboarding ? 'Skip' : 'Back'}
        </Button>
      </Flex>

      <Flex
        direction="column"
        align="center"
        flex="1"
        p={4}
        pb={8}
      >
        <VStack spacing={6} maxW="500px" width="100%" flex="1">
          {/* Header */}
          <Heading size="lg" color="white">
            Connect with Members
          </Heading>

          {/* Sync Contacts Button (native only) - always visible */}
          {isNative && (
            <Button
              width="100%"
              size="lg"
              bg="purple.500"
              color="white"
              leftIcon={<Icon as={ContactRound} boxSize={5} />}
              onClick={handleSyncContacts}
              isLoading={isMatchingContacts}
              loadingText="Syncing..."
              _hover={{ bg: 'purple.600' }}
            >
              Sync Contacts
            </Button>
          )}

          {/* Search Input */}
          <InputGroup>
            <InputLeftElement pointerEvents="none">
              <Icon as={Search} color="gray.400" />
            </InputLeftElement>
            <Input
              placeholder="Search by name..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              bg="whiteAlpha.100"
              border="1px solid"
              borderColor="whiteAlpha.200"
              _focus={{
                borderColor: 'purple.500',
                boxShadow: '0 0 0 1px var(--chakra-colors-purple-500)',
              }}
              data-testid="friends-search-input"
            />
          </InputGroup>

          {/* User List */}
          <VStack
            width="100%"
            spacing={2}
            align="stretch"
            flex="1"
            bg="transparent"
          >
            {/* Loading state */}
            {(isLoadingSuggestions || isSearching || isMatchingContacts) && (
              <Flex justify="center" py={8}>
                <Spinner size="lg" color="purple.500" />
              </Flex>
            )}

            {/* Section Header for Contact Matches */}
            {allContactMatches.length > 0 && searchQuery.length < 2 && (
              <HStack spacing={2} py={2}>
                <Icon as={ContactRound} color="purple.400" boxSize={4} />
                <Text fontSize="sm" fontWeight="semibold" color="purple.400">
                  From Contacts
                </Text>
              </HStack>
            )}

            {/* Contact Matches */}
            {searchQuery.length < 2 &&
              allContactMatches.map((user) => (
                <UserListItem
                  key={user.id}
                  user={user}
                  isSelected={selectedUserIds.has(user.id)}
                  onToggle={() => toggleUserSelection(user.id)}
                />
              ))}

            {/* Section Header for Suggestions */}
            {suggestions.length > 0 && searchQuery.length < 2 && (
              <HStack spacing={2} py={2} mt={allContactMatches.length > 0 ? 2 : 0}>
                <Icon as={Sparkles} color="orange.400" boxSize={4} />
                <Text fontSize="sm" fontWeight="semibold" color="orange.400">
                  Suggested for You
                </Text>
              </HStack>
            )}

            {/* Suggestions or Search Results */}
            {!isLoadingSuggestions &&
              !isSearching &&
              (searchQuery.length >= 2 ? searchResults : suggestions)
                .filter((user: UserSearchResult) => !allContactMatches.some((c) => c.id === user.id))
                .map((user: UserSearchResult) => (
                  <UserListItem
                    key={user.id}
                    user={user}
                    isSelected={selectedUserIds.has(user.id)}
                    onToggle={() => toggleUserSelection(user.id)}
                  />
                ))}

            {/* Empty state */}
            {!isLoadingSuggestions &&
              !isSearching &&
              !isMatchingContacts &&
              displayUsers.length === 0 && (
                <VStack spacing={4} py={8}>
                  <Icon as={Users} boxSize={12} color="gray.500" />
                  <Text color="var(--canvas-text-2)" textAlign="center">
                    {searchQuery.length >= 2
                      ? 'No members found'
                      : 'No suggestions available'}
                  </Text>
                </VStack>
              )}
          </VStack>

          {/* Action Buttons */}
          <VStack width="100%" spacing={3} pt={4}>
            {selectedUserIds.size > 0 && (
              <Text fontSize="sm" color="gray.400">
                {selectedUserIds.size} user{selectedUserIds.size > 1 ? 's' : ''} selected
              </Text>
            )}
            <Button
              size="lg"
              width="100%"
              bg="purple.500"
              color="white"
              _hover={{ bg: 'purple.600' }}
              onClick={handleFollowSelected}
              isLoading={isFollowing}
              rightIcon={<ChevronRight size={20} />}
              data-testid="friends-continue"
            >
              {selectedUserIds.size > 0
                ? `Follow & Continue`
                : 'Continue'}
            </Button>
          </VStack>
        </VStack>
      </Flex>
    </PageWrapper>
  );
}

interface UserListItemProps {
  user: UserSearchResult;
  isSelected: boolean;
  onToggle: () => void;
}

function UserListItem({ user, isSelected, onToggle }: UserListItemProps) {
  const { isOpen, onOpen, onClose } = useDisclosure();

  const handleCompatibilityClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    onOpen();
  };

  return (
    <>
      <HStack
        width="100%"
        bg={isSelected ? 'gray.100' : 'white'}
        borderRadius="lg"
        p={3}
        cursor="pointer"
        onClick={onToggle}
        transition="all 0.2s"
        border="2px solid"
        borderColor={isSelected ? 'purple.500' : 'gray.200'}
        _hover={{ bg: 'gray.50' }}
        align="flex-start"
        data-testid={`user-list-item-${user.id}`}
      >
        <Checkbox
          isChecked={isSelected}
          onChange={onToggle}
          colorScheme="purple"
          onClick={(e) => e.stopPropagation()}
          mt={1}
        />
        <Avatar
          size="md"
          src={user.photoUrl || undefined}
          name={user.name}
          bg="purple.500"
        />
        <VStack align="flex-start" flex={1} gap={1}>
          <Text fontWeight="medium" color="gray.900">
            {user.name}
          </Text>
          {user.bio && (
            <Text fontSize="sm" color="gray.600" noOfLines={2}>
              {user.bio}
            </Text>
          )}
          {!user.bio && user.location && (
            <Text fontSize="sm" color="gray.500">
              {user.location}
            </Text>
          )}
          {/* TasteMatch Pill */}
          {user.compatibilityScore != null && (
            <Box onClick={handleCompatibilityClick}>
              <CompatibilityScore
                score={user.compatibilityScore}
                onClick={onOpen}
                variant="light"
              />
            </Box>
          )}
        </VStack>
        {user.isFollowing && (
          <Text fontSize="xs" color="gray.500" mt={1}>
            Following
          </Text>
        )}
      </HStack>

      {/* TasteMatch Explanation Modal */}
      <TasteMatchExplanation
        isOpen={isOpen}
        onClose={onClose}
        score={user.compatibilityScore}
        memberName={user.name}
      />
    </>
  );
}

export default OnboardingFindFriends;
